import React, { useEffect } from "react";
import { FadeIn } from "components/shared/FadeIn";
import { gsap } from "gsap";
import { useScrollAnimation } from "hooks/useGSAP";

interface PageHeroProps {
  eyebrow: string;
  title: string;
  subtitle?: string;
  description: string;
  media?: React.ReactNode;
  actions?: React.ReactNode;
  highlights?: string[];
  reverse?: boolean;
}

export default function PageHero({
  eyebrow,
  title,
  subtitle,
  description,
  media,
  actions,
  highlights = [],
  reverse = false,
}: Readonly<PageHeroProps>): React.ReactElement {
  const textOrderClass = reverse ? "lg:order-2" : "lg:order-1";
  const mediaOrderClass = reverse ? "lg:order-1" : "lg:order-2";
  const { ref, animate } = useScrollAnimation<HTMLElement>();

  useEffect(() => {
    animate(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          start: "top center",
          end: "bottom center",
          scrub: true,
          // markers: true, // enable for debugging
        },
      });

      tl.to(".page-hero-media", { y: -80, ease: "none" });

      gsap.fromTo(
        ".page-hero-title",
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: ".page-hero-title",
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        },
      );
    });
  }, [animate, ref]);

  return (
    <FadeIn duration={800} distance="24px">
      <section
        ref={ref}
        className="page-hero-surface relative overflow-hidden border-y border-border py-10 sm:py-14 lg:py-16"
      >
        <div className="relative grid items-center grid-cols-1 gap-10 lg:grid-cols-[1fr_0.9fr] lg:gap-16 px-1 sm:px-2">
          <div className={`space-y-6 ${textOrderClass}`}>
            <div className="inline-flex items-center gap-2 rounded-md border border-accent/20 bg-accent-dim px-3 py-1.5 text-[10px] font-mono tracking-[0.16em] uppercase text-accent">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              {eyebrow}
            </div>

            <div className="space-y-4">
              <h1 className="page-hero-title max-w-3xl text-4xl font-bold leading-tight tracking-normal sm:text-5xl lg:text-6xl font-display text-text-primary">
                {title}
              </h1>
              {subtitle && (
                <p className="text-lg font-semibold sm:text-xl text-accent">
                  {subtitle}
                </p>
              )}
              <p className="max-w-2xl text-base leading-relaxed sm:text-lg text-text-secondary">
                {description}
              </p>
            </div>

            {highlights.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {highlights.map((highlight) => (
                  <span
                    key={highlight}
                    className="rounded-md border border-border bg-bg-surface/80 px-3 py-1 text-xs font-mono tracking-wider uppercase text-text-muted"
                  >
                    {highlight}
                  </span>
                ))}
              </div>
            )}

            {actions && (
              <div className="flex flex-wrap gap-3 pt-2">{actions}</div>
            )}
          </div>

          {media && (
            <div className={`relative ${mediaOrderClass}`}>
              <div className="surface-sheen relative overflow-hidden rounded-lg border border-border bg-bg-surface/80 p-4 shadow-card sm:p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1.5">
                    {["#FF5F57", "#FFBD2E", "#28CA41"].map((color) => (
                      <span
                        key={color}
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ background: color }}
                      />
                    ))}
                  </div>
                  <span className="text-[10px] font-mono tracking-[0.16em] uppercase text-text-muted">
                    Preview
                  </span>
                </div>
                <div className="page-hero-media relative z-10 flex items-center justify-center [&>*]:max-w-full [&>*]:h-auto">
                  {media}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </FadeIn>
  );
}
